/**
 * The look of the results page's hero card (ResultsVisualCard): its corners,
 * background, border and shadow, the glass of the price box and the summary
 * strip, and — while a designed room waits for its first photoreal picture —
 * the animation played over the quick still. Set in Admin → Settings →
 * Estimate output (app setting `estimateCardStyle`), previewed live there.
 *
 * `scope` says where the card styling applies: a designed room's estimate
 * only (the advanced output), or every estimate. The wait effect only ever
 * concerns a designed room, since only those render.
 *
 * The card reads the style as CSS custom properties (cardStyleVars) so the
 * stylesheet stays the one source of layout; the admin preview sets the same
 * properties on the same classes.
 */

export type CardStyleScope = 'advanced' | 'all';

/** The animation over the quick still while the first photoreal picture is on its way. */
export type WaitEffect = 'none' | 'breathe' | 'develop' | 'scan' | 'pulse' | 'grain' | 'focus' | 'sweep';

export const WAIT_EFFECTS: { id: WaitEffect; label: string; description: string }[] = [
  { id: 'none', label: 'None', description: 'The still as it is, with only the progress line.' },
  { id: 'breathe', label: 'Breathe', description: 'A slow, gentle zoom in and out, like a held breath.' },
  { id: 'develop', label: 'Developing', description: 'The still fades between a washed-out print and full colour, as a photo develops.' },
  { id: 'scan', label: 'Scan', description: 'A soft band of light passes down the picture every few seconds.' },
  { id: 'pulse', label: 'Pulse', description: 'A glow swells from the centre of the picture and fades at its edges.' },
  { id: 'grain', label: 'Film grain', description: 'Drifting grain over a slightly warmed still, like film waiting to be printed.' },
  { id: 'focus', label: 'Focus', description: 'The picture drifts softly in and out of focus.' },
  { id: 'sweep', label: 'Sweep', description: 'A diagonal highlight glides across the picture, the classic loading shimmer.' },
];

export interface CardShadow {
  x: number;
  y: number;
  blur: number;
  spread: number;
  color: string;
  opacity: number;
}

export interface EstimateCardStyle {
  scope: CardStyleScope;
  /** Corner radius of the card, px. */
  radius: number;
  /** The card's own background (seen at the edges and under a letterboxed picture). */
  background: string;
  borderWidth: number;
  borderColor: string;
  borderOpacity: number;
  shadow: CardShadow;
  /** The soft light inside the card's edge (the present look's white inner glow). */
  glowSize: number;
  glowColor: string;
  glowOpacity: number;
  /** Behind the picture itself while it loads. */
  stageBackground: string;
  /** The slate price box: colour, how solid, and how much it blurs what is behind it. */
  priceBg: string;
  priceOpacity: number;
  priceBlur: number;
  /** The summary strip (room and SlatWall configuration). */
  summaryBg: string;
  summaryOpacity: number;
  summaryBlur: number;
  waitEffect: WaitEffect;
}

export const DEFAULT_CARD_STYLE: EstimateCardStyle = {
  scope: 'advanced',
  radius: 16,
  background: '#1a1a2e',
  borderWidth: 0,
  borderColor: '#3a4b5c',
  borderOpacity: 0.45,
  shadow: { x: 0, y: 0, blur: 5, spread: 5, color: '#000000', opacity: 0.15 },
  glowSize: 10,
  glowColor: '#ffffff',
  glowOpacity: 0.4,
  stageBackground: '#1a1a2e',
  priceBg: '#3a4b5c',
  priceOpacity: 0.9,
  priceBlur: 6,
  summaryBg: '#ffffff',
  summaryOpacity: 0.85,
  summaryBlur: 6,
  waitEffect: 'breathe',
};

const HEX = /^#[0-9a-f]{6}$/i;

const num = (v: unknown, lo: number, hi: number, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
};
const hex = (v: unknown, fallback: string): string => (typeof v === 'string' && HEX.test(v.trim()) ? v.trim().toLowerCase() : fallback);

/** A style from anywhere (the server, a cache, an older save): every field checked, the rest the default. */
export function normalizeCardStyle(raw: unknown): EstimateCardStyle {
  const r = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  const s = (r.shadow && typeof r.shadow === 'object' ? r.shadow : {}) as Record<string, unknown>;
  const D = DEFAULT_CARD_STYLE;
  return {
    scope: r.scope === 'all' ? 'all' : 'advanced',
    radius: Math.round(num(r.radius, 0, 48, D.radius)),
    background: hex(r.background, D.background),
    borderWidth: Math.round(num(r.borderWidth, 0, 12, D.borderWidth) * 2) / 2,
    borderColor: hex(r.borderColor, D.borderColor),
    borderOpacity: num(r.borderOpacity, 0, 1, D.borderOpacity),
    shadow: {
      x: Math.round(num(s.x, -40, 40, D.shadow.x)),
      y: Math.round(num(s.y, -40, 40, D.shadow.y)),
      blur: Math.round(num(s.blur, 0, 80, D.shadow.blur)),
      spread: Math.round(num(s.spread, -20, 40, D.shadow.spread)),
      color: hex(s.color, D.shadow.color),
      opacity: num(s.opacity, 0, 1, D.shadow.opacity),
    },
    glowSize: Math.round(num(r.glowSize, 0, 40, D.glowSize)),
    glowColor: hex(r.glowColor, D.glowColor),
    glowOpacity: num(r.glowOpacity, 0, 1, D.glowOpacity),
    stageBackground: hex(r.stageBackground, D.stageBackground),
    priceBg: hex(r.priceBg, D.priceBg),
    priceOpacity: num(r.priceOpacity, 0, 1, D.priceOpacity),
    priceBlur: Math.round(num(r.priceBlur, 0, 40, D.priceBlur)),
    summaryBg: hex(r.summaryBg, D.summaryBg),
    summaryOpacity: num(r.summaryOpacity, 0, 1, D.summaryOpacity),
    summaryBlur: Math.round(num(r.summaryBlur, 0, 40, D.summaryBlur)),
    waitEffect: WAIT_EFFECTS.some((w) => w.id === r.waitEffect) ? (r.waitEffect as WaitEffect) : D.waitEffect,
  };
}

/** #rrggbb at an opacity, as CSS. */
export function rgba(hexColor: string, opacity: number): string {
  const h = HEX.test(hexColor) ? hexColor.slice(1) : '000000';
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.round(Math.min(1, Math.max(0, opacity)) * 1000) / 1000})`;
}

/** Whether the card styling applies to a card: always to a designed room's, to every card when the scope says so. */
export function cardStyleApplies(style: EstimateCardStyle, advanced: boolean): boolean {
  return advanced || style.scope === 'all';
}

/**
 * The style as the card's custom properties (ResultsVisualCard.css reads
 * them with the present look as each one's fallback).
 */
export function cardStyleVars(style: EstimateCardStyle): React.CSSProperties {
  const sh = style.shadow;
  const parts: string[] = [];
  if (style.glowSize > 0 && style.glowOpacity > 0) parts.push(`inset 0 0 ${style.glowSize}px ${Math.round(style.glowSize * 0.7)}px ${rgba(style.glowColor, style.glowOpacity)}`);
  if (style.borderWidth > 0 && style.borderOpacity > 0) parts.push(`0 0 0 ${style.borderWidth}px ${rgba(style.borderColor, style.borderOpacity)}`);
  if ((sh.blur > 0 || sh.spread !== 0 || sh.x !== 0 || sh.y !== 0) && sh.opacity > 0) parts.push(`${sh.x}px ${sh.y}px ${sh.blur}px ${sh.spread}px ${rgba(sh.color, sh.opacity)}`);
  return {
    '--ecard-radius': `${style.radius}px`,
    '--ecard-bg': style.background,
    '--ecard-shadow': parts.length ? parts.join(', ') : 'none',
    '--ecard-stage-bg': style.stageBackground,
    // A partner portal's own slate (--partner-estimate-bg) still wins over the colour; the opacity is this style's.
    '--ecard-price-bg': `color-mix(in srgb, var(--partner-estimate-bg, ${style.priceBg}) ${Math.round(style.priceOpacity * 100)}%, transparent)`,
    '--ecard-price-blur': `${style.priceBlur}px`,
    '--ecard-summary-bg': rgba(style.summaryBg, style.summaryOpacity),
    '--ecard-summary-blur': `${style.summaryBlur}px`,
  } as React.CSSProperties;
}
